import { Bracket } from "@portal-frontend-ssr/blast-api";
import { useBreakpoints } from "@portal-frontend-ssr/ui";
import classNames from "classnames";
import { Fragment, createContext, useMemo, useState } from "react";
import { ArrowKeys, arrows } from "./arrows";
import { SwissGroup } from "./components/SwissGroup";
import { columnArrows } from "./helpers/getColumnArrows";

function convertScores(scores: string[]): string[] {
  let wonCount = 0;
  let lossCount = 0;
  return scores.map((score) => {
    if (score === "won") {
      wonCount++;
      return `${wonCount}:${lossCount}`;
    } else {
      lossCount++;
      return `${wonCount}:${lossCount}`;
    }
  });
}

export const SwissTournamentContext = createContext<
  { setHoveredTeamUuid: (hoveredTeamUuid?: string) => void; hoveredTeamUuid?: string } | undefined
>(undefined);

const Arrows = ({ index, highlightedTeamPath }: { index: number; highlightedTeamPath: string[] | null }) => {
  const { isMobile } = useBreakpoints();
  if (index === 0) return null;

  const getArrow = (color: string): ArrowKeys => {
    if (color === "red") {
      if (isMobile) {
        return "mobileRedArrow";
      } else {
        return "desktopRedArrow";
      }
    } else {
      if (isMobile) {
        return "mobileGreenArrow";
      } else {
        return "desktopGreenArrow";
      }
    }
  };

  return (
    <div
      className="flex w-8 flex-col place-content-center items-center space-y-20 first:w-auto lg:space-y-12"
      key={`arrow-${index}`}
    >
      {columnArrows[index - 1].map((arrow, index) => (
        <div
          className={classNames("mb-12 w-full scale-75", {
            "opacity-0":
              highlightedTeamPath &&
              !(highlightedTeamPath.includes(arrow.nextGroup) && highlightedTeamPath.includes(arrow.previousGroup)),
          })}
          key={arrow.previousGroup + index}
        >
          {arrows[getArrow(arrow.color)]}
        </div>
      ))}
    </div>
  );
};

export function SwissBracketComponent({ bracket }: { bracket: Bracket; collapsible?: boolean; headerText?: string }) {
  const columns = useMemo(() => {
    if (!bracket || !bracket.groups) return;
    return [
      [bracket.groups[0]],
      bracket.groups.slice(1, 3),
      bracket.groups.slice(3, 6),
      bracket.groups.slice(6, 8),
      [bracket.groups[8]],
    ];
  }, [bracket]);
  const [hoveredTeamUuid, setHoveredTeamUuid] = useState<string | undefined>();

  const highlightedTeamPath = useMemo(() => {
    if (!hoveredTeamUuid) return null;
    if (!columns) return null;
    const path: ("won" | "loss")[] = [];
    columns.map((column) => {
      column.map((group) => {
        group.matches.map((match) => {
          if (!match.isCompleted) return;
          const teamAScore = match.teamAScore;
          const teamBScore = match.teamBScore;
          if (match.teamA?.uuid === hoveredTeamUuid) {
            if (teamAScore > teamBScore) {
              path.push("won");
            } else {
              path.push("loss");
            }
          }
          if (match.teamB?.uuid === hoveredTeamUuid) {
            if (teamBScore > teamAScore) {
              path.push("won");
            } else {
              path.push("loss");
            }
          }
        });
      });
    });
    return ["0:0", ...convertScores(path)];
  }, [hoveredTeamUuid, columns]);

  return (
    <SwissTournamentContext.Provider
      value={{
        setHoveredTeamUuid,
        hoveredTeamUuid,
      }}
    >
      <div className="custom-scrollbar m-auto flex w-full flex-1 flex-row items-center gap-x-2 overflow-x-auto px-8 lg:gap-x-0">
        {!!columns &&
          columns.map((columns, index) => (
            <Fragment key={index}>
              <Arrows index={index} highlightedTeamPath={highlightedTeamPath} />
              <div className="flex grow flex-col" key={`group-${index}`}>
                {columns.map((group) => (
                  <SwissGroup key={group.id} group={group} playoffs={bracket} />
                ))}
              </div>
            </Fragment>
          ))}
      </div>
    </SwissTournamentContext.Provider>
  );
}
